import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { AdminOrderService } from '../../../services/admin-order.service';
import { PdfExportService } from '../../../services/pdf-export.service';
import { ProductLocalizationService } from '../../../services/product-localization.service';
import { ToastService } from '../../../services/toast.service';

export interface AdminOrder {
  id: string;
  userId: string;
  userName: string;
  userMobile: string;
  userEmail?: string;
  userGender?: 'male' | 'female' | 'other';
  userCurrentWeight?: number;
  userCurrentHeight?: number;
  userDateOfBirth?: Date;
  userFullAddress?: string;
  userAltMobile?: string;
  userRegistrationDate?: Date;
  userRecentPhoto?: string;
  userAvatar?: string;
  userTakenDose?: number;
  userCompletedDoses?: string[];
  products: any[];
  totalAmount: number;
  discountAmount?: number;
  deliveryCharges?: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  orderDate: any;
  confirmedDate?: any;
  shippingDate?: any;
  deliveredDate?: any;
  deliveryAddress: string;
  paymentMethod: string;
  paymentStatus: 'pending' | 'completed' | 'failed';
  doseTypes: string[];
  isComboOrder: boolean;
  trackingNumber?: string;
  shippingProvider?: string;
  estimatedDelivery?: any;
  notes?: string;
  // Shiprocket fields
  shiprocketOrderId?: string;
  shiprocketShipmentId?: string;
  shiprocketAwbCode?: string;
  shiprocketCourierName?: string;
  shiprocketManifestUrl?: string;
  shiprocketLabelUrl?: string;
  // Selected courier during checkout
  selectedCourier?: {
    id?: number;
    courierId: number;
    courierName: string;
    deliveryRate: number;
    estimatedDeliveryDays: number;
    codAvailable: boolean;
  };
  shiprocketInvoiceUrl?: string;
}

@Component({
  selector: 'app-order-management',
  templateUrl: './order-management.component.html',
  styleUrls: ['./order-management.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbPaginationModule
  ]
})
export class OrderManagementComponent implements OnInit, OnDestroy {
  orders: AdminOrder[] = [];
  filteredOrders: AdminOrder[] = [];
  loading = false;
  error: string | null = null;
  selectedOrder: AdminOrder | null = null;
  statusUpdateForm: FormGroup;
  exporting = false;
  isEditMode = false;
  editingOrder: AdminOrder | null = null;
  
  // Courier selection
  showCourierModal = false;
  availableCouriers: any[] = [];
  selectedCourierId: number | null = null;
  loadingCouriers = false;
  orderForShipment: AdminOrder | null = null;
  
  // Data table properties
  page = 1;
  pageSize = 20;
  collectionSize = 0;
  sortColumn = 'orderDate';
  sortDirection = 'desc';
  
  // Search and filter form
  searchForm: FormGroup;
  
  private destroy$ = new Subject<void>();

  constructor(
    private adminOrderService: AdminOrderService,
    private fb: FormBuilder,
    private router: Router,
    private pdfExportService: PdfExportService,
    private productLocalizationService: ProductLocalizationService,
    private toastService: ToastService
  ) {
    this.statusUpdateForm = this.fb.group({
      status: [''],
      trackingNumber: [''],
      shippingProvider: [''],
      estimatedDelivery: [''],
      notes: ['']
    });

    this.searchForm = this.fb.group({
      searchTerm: [''],
      status: [''], 
      paymentStatus: [''],
      dateRange: ['']
    });
  }

  ngOnInit(): void {
    this.loadOrders();
    this.setupSearchListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchListener(): void {
    this.searchForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.page = 1; // Reset to first page when filters change
        this.filterOrders();
      });
  }

  private async loadOrders(): Promise<void> {
    try {
      this.loading = true;
      this.error = null;

      // Load all orders for client-side filtering and pagination
      const result = await this.adminOrderService.getAdminOrders(1000).toPromise();
      if (result) {
        this.orders = result.orders;
        
        // Enrich products with i18n data from database
        for (const order of this.orders) {
          if (order.products && Array.isArray(order.products)) {
            order.products = await Promise.all(
              order.products.map(product => this.productLocalizationService.enrichProductWithI18n(product))
            );
          }
        }
        
        this.collectionSize = this.orders.length;
        this.filterOrders();
        if (this.orders.length > 0) {
          this.toastService.info(`Loaded ${this.orders.length} orders successfully`);
        }
        }
      } catch (error) {
        this.error = 'Failed to load orders';
        this.toastService.error('Failed to load orders. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  private filterOrders(): void {
    const filters = this.searchForm.value;
    
    this.filteredOrders = this.orders.filter(order => {
      // Search term filter - search across multiple fields
      if (filters.searchTerm && filters.searchTerm.trim()) {
        const searchLower = filters.searchTerm.toLowerCase().trim();
        const searchableText = [
          order.id,
          order.userName,
          order.userMobile,
          order.deliveryAddress,
          this.getProductNames(order),
          order.status,
          order.paymentStatus
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(searchLower)) {
          return false;
        }
      }

      // Status filter
      if (filters.status && filters.status.trim()) {
        if (order.status !== filters.status) {
          return false;
        }
      }

      // Payment status filter
      if (filters.paymentStatus && filters.paymentStatus.trim()) {
        if (order.paymentStatus !== filters.paymentStatus) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateRange && filters.dateRange.trim()) {
        const orderDate = this.parseOrderDate(order.orderDate);
        if (!orderDate) return false;
        
        const now = new Date();
        let startDate = new Date();
        
        switch (filters.dateRange) {
          case '1':
            startDate.setDate(now.getDate() - 1);
            break;
          case '7':
            startDate.setDate(now.getDate() - 7);
            break;
          case '30':
            startDate.setDate(now.getDate() - 30);
            break;
          case '90':
            startDate.setDate(now.getDate() - 90);
            break;
          default:
            startDate = new Date(0);
        }
        
        if (orderDate < startDate || orderDate > now) {
          return false;
        }
      }

      return true;
    });

    // Sort the filtered results
    this.sortOrders();
    
    // Update collection size for pagination
    this.collectionSize = this.filteredOrders.length;
    
    // Reset to first page when filters change
    this.page = 1;
  }

  private parseOrderDate(dateValue: any): Date | null {
    if (!dateValue) return null;
    
    try {
      // Handle Firestore Timestamp
      if (dateValue.seconds) {
        return new Date(dateValue.seconds * 1000);
      }
      
      // Handle regular Date object or string
      if (dateValue instanceof Date) {
        return dateValue;
      }
      
      // Handle ISO string or other date formats
      const parsed = new Date(dateValue);
      if (isNaN(parsed.getTime())) {
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.warn('Failed to parse date:', dateValue, error);
      return null;
    }
  }

  private sortOrders(): void {
    this.filteredOrders.sort((a, b) => {
      let aValue: any = a[this.sortColumn as keyof AdminOrder];
      let bValue: any = b[this.sortColumn as keyof AdminOrder];

      // Handle date sorting
      if (this.sortColumn === 'orderDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      let comparison = 0;
      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      return this.sortDirection === 'desc' ? -comparison : comparison;
    });
  }

  onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortOrders();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'fas fa-sort';
    }
    return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  }

  getFilteredOrdersCount(): number {
    return this.filteredOrders.length;
  }

  getTotalOrdersCount(): number {
    return this.orders.length;
  }

  clearAllFilters(): void {
    this.searchForm.patchValue({
      searchTerm: '',
      status: '',
      paymentStatus: '',
      dateRange: ''
    });
    this.filterOrders();
    this.toastService.info('All filters cleared');
  }

  resetFiltersToDefault(): void {
    this.searchForm.patchValue({
      searchTerm: '',
      status: 'pending', // Keep pending as default
      paymentStatus: '',
      dateRange: ''
    });
    this.filterOrders();
    this.toastService.info('Filters reset to default');
  }

  async exportOrders() {
    try {
      if (this.filteredOrders.length === 0) {
        this.toastService.warning('No orders available for export');
        return;
      }
      
      this.exporting = true;
      // Export first order as example (you can modify this to export all orders)
      await this.pdfExportService.exportOrderToPDF(this.filteredOrders[0]);
      this.toastService.success(`Order ${this.filteredOrders[0].id} exported successfully!`);
    } catch (error) {
      console.error('Error exporting orders:', error);
      this.toastService.error('Failed to export order. Please try again.');
    } finally {
      this.exporting = false;
    }
  }

  retry() {
    this.toastService.info('Retrying to load orders...');
    this.loadOrders();
  }

  t(key: string, params?: any): string {
    // this.languageService.translate(key, params); // This line was removed
    return key; // Placeholder for now
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'shipped': 'status-shipped',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled'
    };
    return statusClasses[status] || 'status-default';
  }

  getStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      'pending': 'Pending',
      'confirmed': 'Confirmed',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return statusTexts[status] || status;
  }

  getPaymentStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'pending': 'payment-pending',
      'completed': 'payment-completed',
      'failed': 'payment-failed'
    };
    return statusClasses[status] || 'payment-default';
  }

  getPaymentStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      'pending': 'Pending',
      'completed': 'Completed',
      'failed': 'Failed'
    };
    return statusTexts[status] || status;
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  getOrderTotal(order: AdminOrder): number {
    return order.totalAmount - (order.discountAmount || 0);
  }

  getProductAmount(order: AdminOrder): number {
    return order.totalAmount || 0;
  }

  getDeliveryCharges(order: AdminOrder): number {
    return order.deliveryCharges || 0;
  }

  getGrandTotal(order: AdminOrder): number {
    const productAmount = this.getProductAmount(order);
    const deliveryCharges = this.getDeliveryCharges(order);
    const discountAmount = order.discountAmount || 0;
    return productAmount + deliveryCharges - discountAmount;
  }

  getProductNames(order: AdminOrder): string {
    return order.products.map((p: any) => this.productLocalizationService.getLocalizedName(p)).join(', ');
  }

  getDoseClass(doseType: string): string {
    switch (doseType?.toLowerCase()) {
      case 'dose one':
      case 'dose 1':
      case '1':
        return 'dose-one';
      case 'dose two':
      case 'dose 2':
      case '2':
        return 'dose-two';
      case 'dose three':
      case 'dose 3':
      case '3':
        return 'dose-three';
      default:
        return 'dose-default';
    }
  }

  getDoseImage(doseType: string): string {
    switch (doseType?.toLowerCase()) {
      case 'dose one':
      case 'dose 1':
      case '1':
        return 'https://placehold.co/400x400/66BB6A/31343C?font=poppins&text=Dose+One';
      case 'dose two':
      case 'dose 2':
      case '2':
        return 'https://placehold.co/400x400/FFB74D/31343C?font=poppins&text=Dose+Two';
      case 'dose three':
      case 'dose 3':
      case '3':
        return 'https://placehold.co/400x400/D32F2F/31343C?font=poppins&text=Dose+Three';
      default:
        return 'https://placehold.co/400x400/9E9E9E/31343C?font=poppins&text=Product';
    }
  }

  viewOrderDetails(orderId: string) {
    this.router.navigate(['/admin/orders', orderId]);
  }

  openStatusUpdateModal(order: AdminOrder) {
    this.selectedOrder = order;
    this.statusUpdateForm.patchValue({
      status: order.status,
      trackingNumber: order.trackingNumber || '',
      shippingProvider: order.shippingProvider || '',
      estimatedDelivery: order.estimatedDelivery ? 
        new Date(order.estimatedDelivery.seconds * 1000).toISOString().split('T')[0] : '',
      notes: order.notes || ''
    });
  }

  editOrder(order: AdminOrder) {
    this.editingOrder = { ...order };
    this.isEditMode = true;
    
    // Set estimated delivery date for the date input
    if (this.editingOrder.estimatedDelivery) {
      const date = this.editingOrder.estimatedDelivery.seconds ? 
        new Date(this.editingOrder.estimatedDelivery.seconds * 1000) : 
        new Date(this.editingOrder.estimatedDelivery);
      this.editingOrder.estimatedDelivery = date;
    }
  }

  cancelEdit() {
    this.isEditMode = false;
    this.editingOrder = null;
  }

  async saveEdit() {
    if (!this.editingOrder) return;

    try {
      this.loading = true;
      
      // Validate required fields
      if (this.editingOrder.status === 'shipped' && !this.editingOrder.trackingNumber && !this.editingOrder.shiprocketShipmentId) {
        this.toastService.warning('Tracking number is required when status is set to shipped');
        return;
      }

      // Store previous fields for rollback
      const orderIndex = this.orders.findIndex(o => o.id === this.editingOrder!.id);
      const previousStatus = orderIndex !== -1 ? this.orders[orderIndex].status : this.editingOrder.status;
      const previousShippingDate = this.editingOrder.shippingDate;
      const previousEstimatedDelivery = this.editingOrder.estimatedDelivery;
      const previousProvider = this.editingOrder.shippingProvider;
      
      // If status is being changed to "shipped", create Shiprocket shipment FIRST
      if (this.editingOrder.status === 'shipped' && previousStatus !== 'shipped') {
        // If shipment doesn't exist, create it before updating status
        if (!this.editingOrder.shiprocketShipmentId) {
          this.toastService.info('Creating Shiprocket shipment...');
          const result = await this.adminOrderService.createShiprocketShipment(
            this.editingOrder.id,
            this.editingOrder
          );
          
          if (!result.success) {
            // Rollback status and UI fields to previous state
            this.editingOrder.status = previousStatus;
            this.editingOrder.shippingDate = previousShippingDate;
            this.editingOrder.estimatedDelivery = previousEstimatedDelivery as any;
            this.editingOrder.shippingProvider = previousProvider || '';
            this.toastService.error(result.error || 'Failed to create shipment. Status not updated.');
            return;
          }
          
          // Update order with Shiprocket details
          this.editingOrder.shiprocketShipmentId = result.shipmentId?.toString();
          this.editingOrder.shiprocketAwbCode = result.awbCode;
          this.editingOrder.shiprocketCourierName = result.courierName;
          this.editingOrder.trackingNumber = result.awbCode;
          this.editingOrder.shippingProvider = 'shiprocket';
          this.toastService.success(`Shipment created successfully! AWB: ${result.awbCode}`);
        }
      }

      // Now update order status (only if Shiprocket APIs succeeded)
      await this.adminOrderService.updateOrderStatus(
        this.editingOrder.id, 
        this.editingOrder.status, 
        this.editingOrder.notes
      );

      // Update tracking info if status is shipped
      if (this.editingOrder.status === 'shipped' && this.editingOrder.trackingNumber) {
        await this.adminOrderService.updateTrackingInfo(this.editingOrder.id, {
          trackingNumber: this.editingOrder.trackingNumber,
          shippingProvider: this.editingOrder.shippingProvider || '',
          estimatedDelivery: this.editingOrder.estimatedDelivery
        });
      }

      // Update local data
      if (orderIndex !== -1) {
        this.orders[orderIndex] = { ...this.editingOrder };
      }
      
      this.filterOrders();
      this.toastService.success('Order updated successfully');
      this.isEditMode = false;
      this.editingOrder = null;
      
    } catch (error) {
      console.error('Error updating order:', error);
      // Rollback UI to previous state on error
      if (this.editingOrder) {
        const orderIndex = this.orders.findIndex(o => o.id === this.editingOrder!.id);
        if (orderIndex !== -1 && this.editingOrder.status === 'shipped') {
          this.editingOrder.status = this.orders[orderIndex].status;
          this.editingOrder.shippingDate = this.orders[orderIndex].shippingDate;
          this.editingOrder.estimatedDelivery = this.orders[orderIndex].estimatedDelivery;
          this.editingOrder.shippingProvider = this.orders[orderIndex].shippingProvider;
        }
      }
      this.toastService.error('Failed to update order. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  onStatusChange() {
    if (!this.editingOrder) return;
    
    const now = new Date();
    
    // Automatically set dates based on status changes
    switch (this.editingOrder.status) {
      case 'confirmed':
        if (!this.editingOrder.confirmedDate) {
          this.editingOrder.confirmedDate = now;
        }
        break;
      case 'shipped':
        this.editingOrder.shippingDate = now;
        if (!this.editingOrder.estimatedDelivery) {
          const estimatedDate = new Date(now);
          estimatedDate.setDate(estimatedDate.getDate() + 4);
          this.editingOrder.estimatedDelivery = estimatedDate;
        }
        // Note: Shiprocket shipment creation will be handled in saveEdit() to ensure status rollback on failure
        break;
      case 'delivered':
        this.editingOrder.deliveredDate = now;
        break;
    }
  }

  async showCourierSelection(order: AdminOrder) {
    this.orderForShipment = order;
    this.loadingCouriers = true;
    this.showCourierModal = true;
    this.selectedCourierId = null;
    
    try {
      const result = await this.adminOrderService.getCourierOptions(order);
      
      if (result.success && result.couriers) {
        this.availableCouriers = result.couriers;
        // Pre-select first courier (usually recommended) as default
        if (result.couriers.length > 0) {
          this.selectedCourierId = result.couriers[0].courier_company_id;
        }
      } else {
        this.toastService.error(result.error || 'Failed to load courier options');
        this.closeCourierModal();
      }
    } catch (error) {
      console.error('Error loading courier options:', error);
      this.toastService.error('Failed to load courier options. Please try again.');
      this.closeCourierModal();
    } finally {
      this.loadingCouriers = false;
    }
  }

  closeCourierModal() {
    this.showCourierModal = false;
    this.availableCouriers = [];
    this.selectedCourierId = null;
    this.orderForShipment = null;
  }

  async confirmCourierSelection() {
    if (!this.orderForShipment || !this.selectedCourierId) {
      this.toastService.error('Please select a courier');
      return;
    }

    try {
      this.loading = true;
      this.toastService.info('Creating Shiprocket shipment...');
      
      const result = await this.adminOrderService.createShiprocketShipment(
        this.orderForShipment.id, 
        this.orderForShipment,
        this.selectedCourierId
      );
      
      if (result.success) {
        // Update local order data
        const orderIndex = this.orders.findIndex(o => o.id === this.orderForShipment!.id);
        if (orderIndex !== -1) {
          this.orders[orderIndex] = {
            ...this.orders[orderIndex],
            shiprocketShipmentId: result.shipmentId?.toString(),
            shiprocketAwbCode: result.awbCode,
            shiprocketCourierName: result.courierName,
            trackingNumber: result.awbCode,
            shippingProvider: 'shiprocket'
          };
          if (this.editingOrder?.id === this.orderForShipment.id) {
            this.editingOrder = { ...this.orders[orderIndex] };
          }
        }
        this.filterOrders();
        this.closeCourierModal();
        this.toastService.success(`Shipment created successfully! AWB: ${result.awbCode}`);
      } else {
        this.toastService.error(result.error || 'Failed to create shipment');
      }
    } catch (error) {
      console.error('Error creating shipment:', error);
      this.toastService.error('Failed to create shipment. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  private async createShiprocketShipmentForOrder(order: AdminOrder) {
    // Show courier selection modal instead of directly creating
    await this.showCourierSelection(order);
  }

  async updateOrderStatus() {
    if (!this.selectedOrder || !this.statusUpdateForm.valid) return;

    try {
      const updateData = this.statusUpdateForm.value;
      const previousStatus = this.selectedOrder.status;
      
      // If status is being changed to "shipped", create Shiprocket shipment FIRST
      if (updateData.status === 'shipped' && previousStatus !== 'shipped') {
        if (!this.selectedOrder.shiprocketShipmentId) {
          // Show courier selection modal instead of directly creating
          await this.showCourierSelection({ ...this.selectedOrder, ...updateData });
          // Don't update status yet - wait for courier selection to complete
          this.statusUpdateForm.patchValue({ status: previousStatus });
          return;
        } else if (updateData.trackingNumber) {
          // Update tracking info for existing shipment
          await this.adminOrderService.updateTrackingInfo(this.selectedOrder.id, {
            trackingNumber: updateData.trackingNumber,
            shippingProvider: updateData.shippingProvider || 'shiprocket',
            estimatedDelivery: updateData.estimatedDelivery ? new Date(updateData.estimatedDelivery) : undefined
          });
        }
      }
      
      // Now update order status (only if Shiprocket APIs succeeded)
      await this.adminOrderService.updateOrderStatus(
        this.selectedOrder.id, 
        updateData.status, 
        updateData.notes
      );
      
      // Update local data
      const orderIndex = this.orders.findIndex(o => o.id === this.selectedOrder!.id);
      if (orderIndex !== -1) {
        this.orders[orderIndex] = { 
          ...this.orders[orderIndex], 
          status: updateData.status,
          notes: updateData.notes,
          trackingNumber: updateData.trackingNumber,
          shippingProvider: updateData.shippingProvider,
          estimatedDelivery: updateData.estimatedDelivery
        };
        this.filterOrders();
      }

      this.toastService.success('Order status updated successfully');
      this.selectedOrder = null;
      this.statusUpdateForm.reset();
    } catch (error) {
      console.error('Error updating order status:', error);
      this.toastService.error('Failed to update order status');
    }
  }

  closeStatusModal() {
    this.selectedOrder = null;
    this.statusUpdateForm.reset();
  }

  hasActiveFilters(): boolean {
    const filters = this.searchForm.value;
    return !!(filters.searchTerm?.trim() || 
              (filters.status && filters.status !== 'pending') || 
              filters.paymentStatus?.trim() || 
              filters.dateRange?.trim());
  }

  getActiveFiltersCount(): number {
    const filters = this.searchForm.value;
    let count = 0;
    
    if (filters.searchTerm?.trim()) count++;
    if (filters.status && filters.status !== 'pending') count++;
    if (filters.paymentStatus?.trim()) count++;
    if (filters.dateRange?.trim()) count++;
    
    return count;
  }

  // Localized product name
  getLocalizedName(product: any): string {
    return this.productLocalizationService.getLocalizedName(product);
  }

  // Localized product description
  getLocalizedDescription(product: any): string {
    return this.productLocalizationService.getLocalizedDescription(product);
  }
} 